import type {
  ActionListItem,
  ActionListOption,
  OptionComponentProps,
} from '@latitude/action-list';
import { DefaultOptionComponent } from '@latitude/action-list';
import { Icon } from '@latitude/icons';
import { Text } from '@latitude/text';

import type { AddItemLabel } from './types';

/**
 * Returns a copy of `options` with every non-selected, non-already-disabled
 * item marked `disabled: true`. Used to enforce a multi-select cap: once the
 * user reaches `maxSelections`, the remaining options become disabled so they
 * can't be picked. Selected items stay interactive so users can de-select to
 * free a slot. Walks both flat items and items nested under header rows.
 */
export const disableNonSelectedOptions = <T,>(
  options: ActionListOption<T>[],
  selectedValues: string[],
): ActionListOption<T>[] => {
  const selected = new Set(selectedValues);
  const maybeDisable = (item: ActionListItem<T>): ActionListItem<T> =>
    item.disabled || selected.has(item.value)
      ? item
      : { ...item, disabled: true };
  return options.map((option) =>
    'items' in option
      ? { ...option, items: option.items.map(maybeDisable) }
      : maybeDisable(option),
  );
};

export const shouldLoadMore = (
  scrollHeight: number,
  clientHeight: number,
  scrollTop: number,
): boolean => {
  const bottomBorder = (scrollHeight - clientHeight) / 4;
  return bottomBorder < scrollTop;
};

// We need to check if the initial value is returned in the initial response
// if so we don't want to append the value. Since items can either be headers
// or just items we need to check both cases
export const findValueInItems = <T = undefined,>(
  options: ActionListOption<T>[],
  value?: string,
): ActionListItem<T> | undefined => {
  let foundItemInSub: ActionListItem<T> | undefined;

  if (!value) {
    return;
  }

  const foundOption = options.find((opt) => {
    if ('value' in opt) {
      return opt.value === value;
    }

    foundItemInSub = opt.items.find((item) => item.value === value);

    return foundItemInSub;
  });

  if (!foundOption) {
    return;
  }

  if ('value' in foundOption) {
    return foundOption;
  }

  return foundItemInSub;
};

export const addItemOption = (
  searchTerm?: string,
  label?: AddItemLabel,
): React.FC<OptionComponentProps<{ custom: boolean }>> => {
  const AddOption = (props: OptionComponentProps<{ custom: boolean }>) => {
    // if there is anything typed in the search box we want to show a different type of option
    return props?.item.value === searchTerm && props?.item.extraData?.custom ? (
      <div
        data-testid="add-new-item-option"
        className="py-spacing-comp-action-list-medium-vertical before:border-color-ref-ui-divider-strong relative flex w-full items-center gap-2 before:absolute before:top-[-4px] before:w-full before:border-t"
      >
        {typeof label === 'function' ? (
          label(searchTerm || '')
        ) : (
          <>
            <Icon name="plus" width="16" height="16" />
            <Text>
              {label
                ? label.replace('$searchTerm', searchTerm || '')
                : `Add ${searchTerm}`}
            </Text>
          </>
        )}
      </div>
    ) : (
      <DefaultOptionComponent {...props} />
    );
  };
  AddOption.displayName = 'AddOption';
  return AddOption;
};
